import {
  Anvil,
  ArrowDownToLine,
  ArrowRightLeft,
  BookOpen,
  Gem,
  Hammer,
  HeartPulse,
  Recycle,
  Sparkles,
  Warehouse,
} from "lucide-react";
import { FormEvent, useState } from "react";
import {
  type ConvertibleResource,
  type GrowthProfile,
  type ResourceConversionResult,
} from "../types";
import { formatDisplayNumber, messageOf } from "../ui/format";
import { ResourceIcon } from "../ui/Resources";

export const CONVERTIBLE_RESOURCES: Array<{ id: ConvertibleResource; label: string }> = [
  { id: "wood", label: "木材" },
  { id: "ore", label: "矿石" },
  { id: "gold", label: "金币" },
];

export function WorkshopOperationSlot({
  building,
  growth,
  busy,
  onConvert,
}: {
  building?: GrowthProfile["buildings"][number];
  growth: GrowthProfile;
  busy: boolean;
  onConvert: (
    sourceCurrency: ConvertibleResource,
    targetCurrency: ConvertibleResource,
    sourceAmount: number,
  ) => Promise<ResourceConversionResult>;
}) {
  const [source, setSource] = useState<ConvertibleResource>("wood");
  const [target, setTarget] = useState<ConvertibleResource>("ore");
  const [amount, setAmount] = useState("100");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<ResourceConversionResult | null>(null);
  if (!building || building.id !== "conversion_table") {
    return (
      <section className="workshop-operation-slot is-passive" aria-label="建筑操作">
        <span className="workshop-operation-icon">
          <WorkshopIcon buildingId={building?.id ?? ""} size={20} />
        </span>
        <div>
          <strong>建筑操作</strong>
          <small>{building?.name ?? "工坊"} · 无额外操作</small>
        </div>
      </section>
    );
  }
  const efficiencyBasisPoints = Math.round(growth.conversion.efficiency * 10_000);
  const sourceAmount = Math.max(0, Math.floor(Number(amount) || 0));
  const targetAmount = Math.floor((sourceAmount * efficiencyBasisPoints) / 10_000);
  const sourceBalance = growth.balances[source];
  const targetRoom = Math.max(0, growth.effects.storageCaps[target] - growth.balances[target]);
  const maxByStorage =
    efficiencyBasisPoints > 0 && targetRoom > 0
      ? Math.ceil(((targetRoom + 1) * 10_000) / efficiencyBasisPoints) - 1
      : 0;
  const maxAmount = Math.max(
    0,
    Math.min(sourceBalance, growth.conversion.remainingToday, maxByStorage),
  );
  const canConvert =
    growth.conversion.unlocked && sourceAmount > 0 && sourceAmount <= maxAmount && targetAmount > 0;
  const chooseSource = (next: ConvertibleResource) => {
    setSource(next);
    if (next === target) setTarget(source);
    setMessage("");
    setResult(null);
  };
  const chooseTarget = (next: ConvertibleResource) => {
    if (next === source) return;
    setTarget(next);
    setMessage("");
    setResult(null);
  };
  const swap = () => {
    setSource(target);
    setTarget(source);
    setMessage("");
    setResult(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canConvert || busy) return;
    setMessage("");
    setResult(null);
    try {
      const converted = await onConvert(source, target, sourceAmount);
      setResult(converted);
      setMessage(converted.replayed ? "转化记录已同步" : "转化完成");
    } catch (error) {
      setMessage(messageOf(error));
    }
  };
  return (
    <section
      className="workshop-operation-slot is-conversion"
      aria-labelledby="workshop-operation-title"
    >
      <div className="workshop-operation-heading">
        <span className="workshop-operation-icon">
          <Recycle size={20} />
        </span>
        <div>
          <strong id="workshop-operation-title">建筑操作</strong>
          <small>
            {growth.conversion.unlocked
              ? `今日 ${growth.conversion.usedToday}/${growth.conversion.dailyInputLimit} · 效率 ${formatDisplayNumber(growth.conversion.efficiency * 100)}%`
              : "转化台 Lv.1 解锁"}
          </small>
        </div>
      </div>
      <form className="conversion-controls" onSubmit={submit}>
        <label>
          <span>投入</span>
          <select
            value={source}
            disabled={busy || !growth.conversion.unlocked}
            onChange={(event) => chooseSource(event.currentTarget.value as ConvertibleResource)}
          >
            {CONVERTIBLE_RESOURCES.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="icon-button conversion-swap"
          title="互换转化方向"
          aria-label="互换转化方向"
          disabled={busy || !growth.conversion.unlocked}
          onClick={swap}
        >
          <ArrowRightLeft size={17} />
        </button>
        <label>
          <span>获得</span>
          <select
            value={target}
            disabled={busy || !growth.conversion.unlocked}
            onChange={(event) => chooseTarget(event.currentTarget.value as ConvertibleResource)}
          >
            {CONVERTIBLE_RESOURCES.map((resource) => (
              <option key={resource.id} value={resource.id} disabled={resource.id === source}>
                {resource.label}
              </option>
            ))}
          </select>
        </label>
        <label className="conversion-amount">
          <span>数量</span>
          <input
            type="number"
            inputMode="numeric"
            min="1"
            max={Math.max(1, maxAmount)}
            step="1"
            value={amount}
            disabled={busy || !growth.conversion.unlocked}
            onChange={(event) => {
              setAmount(event.currentTarget.value);
              setMessage("");
              setResult(null);
            }}
          />
        </label>
        <button
          type="button"
          className="secondary-command compact conversion-max"
          disabled={busy || maxAmount <= 0}
          onClick={() => setAmount(String(maxAmount))}
        >
          最大
        </button>
        <output className="conversion-output" aria-live="polite">
          <ResourceIcon kind={target} size={16} />
          <strong>{targetAmount}</strong>
        </output>
        <button type="submit" className="primary-command compact" disabled={busy || !canConvert}>
          <Recycle size={16} />
          转化
        </button>
      </form>
      {message && (
        <div className={`conversion-status${result ? " is-success" : ""}`} role="status">
          {message}
          {result && (
            <span>
              <ResourceIcon kind={result.targetCurrency} size={13} />+{result.targetAmount}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

export function WorkshopIcon({ buildingId, size = 22 }: { buildingId: string; size?: number }) {
  switch (buildingId) {
    case "altar":
      return <HeartPulse size={size} />;
    case "forge":
      return <Anvil size={size} />;
    case "blacksmith":
      return <Hammer size={size} />;
    case "mine_lamp":
      return <Sparkles size={size} />;
    case "warehouse":
      return <Warehouse size={size} />;
    case "library":
      return <BookOpen size={size} />;
    case "shaft":
      return <ArrowDownToLine size={size} />;
    case "portal":
      return <Gem size={size} />;
    case "conversion_table":
      return <Recycle size={size} />;
    default:
      return <Hammer size={size} />;
  }
}
